import { z } from 'zod';

/**
 * The module that owns a setting's **editor** — the P10.4 hand-off pointer.
 *
 * A setting whose value is a structured domain another module owns (a tier ×
 * capability matrix, a per-category notification grid, the identity-signal
 * policy) is a registry value like any other: scoped, defaulted, resolved by
 * `getSetting`. What it is not is a value the *generic* settings screen can
 * draw — which is why it used to arrive as a JSON textarea, and why U0 forbids
 * that. So the definition names its owner, Settings renders a read-only
 * structured summary and points at the module, and the owning module builds the
 * real editor.
 *
 * This is a **presentation and ownership pointer, never an authorization
 * boundary**: the api keeps accepting a write at a marked key, because that one
 * write path (U9) is what the owning module's editor will use.
 *
 * A closed union of **semantic module names**, mapped in the admin by a `switch`
 * whose default arm is typed `never` — so adding a member here fails
 * `nx typecheck` until a destination is decided for it. Closed from the other
 * side too: the api's registry spec asserts every owner declared here owns at
 * least one setting.
 */
export const SETTING_MANAGED_BY = [
  /** The admin Subscriptions/Billing module (P23 · P26). */
  'billing',
  /** The admin Notifications module (P21). */
  'notifications',
  /** The admin Trust & safety module (P14). */
  'trust',
  /** The Integrations panel (P10.5), where a provider and a model are chosen. */
  'integrations',
] as const;

export const SettingManagedBySchema = z.enum(SETTING_MANAGED_BY);

export type SettingManagedBy = (typeof SETTING_MANAGED_BY)[number];

/** Fail-closed narrowing for a name off the wire. */
export function isSettingManagedBy(value: string): value is SettingManagedBy {
  return SettingManagedBySchema.safeParse(value).success;
}
